import React, { useEffect, useState } from "react";
import { Button, Card, Col, Descriptions, Empty, Form, Input, Row, Select, Spin, Tag } from "antd";
import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ApiURL } from "../../../../services/apiConstants";
import { post } from "../../../../services/services";
import { ErrorToast, SuccessToast } from "../../../../widgets/AppToast";
import { MOCK_MAINTENANCE_LOG_TASKS, USE_MOCK_MAINTENANCE_LOG } from "./mockMaintenanceLog";

const { TextArea } = Input;

const STATUS_COLORS = {
  pending: "gold",
  in_progress: "blue",
  completed: "green",
  overdue: "red",
  cancelled: "default",
};

const getObjectData = (res, fallback) => {
  if (res?.data && !Array.isArray(res.data)) return res.data;
  if (res?.response_data && !Array.isArray(res.response_data)) return res.response_data;
  if (res?.task) return res.task;
  if (res?.job_task) return res.job_task;
  return fallback || null;
};

const formatDate = (date) => {
  if (!date) return "N/A";
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "N/A";
};

const getTaskId = (record) =>
  record?.id || record?.task_id || record?.job_task_id || record?.job_register_task_id;

const getMockTaskById = (id) => {
  if (!USE_MOCK_MAINTENANCE_LOG) return null;
  return MOCK_MAINTENANCE_LOG_TASKS.find((task) => String(getTaskId(task)) === String(id)) || null;
};

const JobTaskView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loader, setLoader] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [task, setTask] = useState(location.state?.taskData || null);

  const fetchTask = async () => {
    if (!id) return;
    setLoader(true);
    try {
      const formData = new FormData();
      formData.append("id", id);
      formData.append("task_id", id);
      formData.append("job_task_id", id);

      const res = await post(ApiURL.view_job_task, formData);
      const nextTask =
        res?.status === true
          ? getObjectData(res, location.state?.taskData || getMockTaskById(id))
          : location.state?.taskData || getMockTaskById(id);
      setTask(nextTask);
      form.setFieldsValue({
        status: nextTask?.status || "pending",
        remarks: nextTask?.remarks || "",
      });
    } catch (error) {
      console.error("Error fetching job task:", error);
      const fallbackTask = location.state?.taskData || getMockTaskById(id);
      setTask(fallbackTask);
      form.setFieldsValue({
        status: fallbackTask?.status || "pending",
        remarks: fallbackTask?.remarks || "",
      });
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchTask();
  }, [id]);

  const handleStatusUpdate = async (values) => {
    if (!id) return;
    setSubmitLoader(true);
    try {
      const userData = JSON.parse(localStorage.getItem("user_data"));
      const formData = new FormData();
      formData.append("id", id);
      formData.append("task_id", id);
      formData.append("job_task_id", id);
      formData.append("status", values.status);
      formData.append("remarks", values.remarks || "");
      formData.append("user_id", userData?.data?.user_id || "");

      const res = await post(ApiURL.update_job_task_status, formData);
      if (res?.status === true) {
        SuccessToast("Success", "Job task updated successfully");
        setTask((prev) => ({ ...prev, status: values.status, remarks: values.remarks || "" }));
      } else if (USE_MOCK_MAINTENANCE_LOG && getMockTaskById(id)) {
        SuccessToast("Success", "Mock job task updated locally");
        setTask((prev) => ({ ...prev, status: values.status, remarks: values.remarks || "" }));
      } else {
        ErrorToast("Error", res?.message || "Unable to update job task");
      }
    } catch (error) {
      console.error("Error updating job task:", error);
      if (USE_MOCK_MAINTENANCE_LOG && getMockTaskById(id)) {
        SuccessToast("Success", "Mock job task updated locally");
        setTask((prev) => ({ ...prev, status: values.status, remarks: values.remarks || "" }));
      } else {
        ErrorToast("Error", "Unable to update job task");
      }
    } finally {
      setSubmitLoader(false);
    }
  };

  const taskType = task?.task_type || task?.maintenance_type;

  return (
    <div className="white-container" style={{ padding: "10px" }}>
      <div className="row" style={{ padding: "10px", marginBottom: 10 }}>
        <div className="col-6">
          <div className="theme-content-title text-left">
            <h3 style={{ margin: 0 }}>Maintenance Log Details</h3>
          </div>
        </div>
        <div className="col-6" style={{ textAlign: "right" }}>
          <Button onClick={() => navigate("/maintenance-log")}>Back to List</Button>
        </div>
      </div>

      {loader ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : task ? (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card title="Task Information" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <Descriptions bordered column={1} size="middle">
                <Descriptions.Item label="Task ID">{getTaskId(task) || id || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Job Code">{task.code || task.job_code || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Ship">{task.ship_name || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Machinery">{task.machinery_name || task.machinary_name || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Task Type">
                  <Tag color={taskType === "running_hours" ? "purple" : "cyan"}>
                    {taskType === "running_hours" ? "RUNNING HOURS" : taskType === "calendar" ? "CALENDAR" : "N/A"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Due">
                  {taskType === "running_hours"
                    ? task.due_hours || task.next_due_hours || "N/A"
                    : formatDate(task.due_date || task.next_due_date)}
                </Descriptions.Item>
                <Descriptions.Item label="Current Counter Reading">
                  {task.current_counter_reading || task.counter_reading || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Priority">{task.priority || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={STATUS_COLORS[String(task.status || "pending").toLowerCase()] || "default"}>
                    {String(task.status || "pending").replaceAll("_", " ").toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Description">{task.job_description || task.description || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Remarks">{task.remarks || "N/A"}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Track Task" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <Form form={form} layout="vertical" onFinish={handleStatusUpdate} initialValues={{ status: task.status || "pending" }}>
                <Form.Item name="status" label="Status" rules={[{ required: true, message: "Please select status" }]}>
                  <Select placeholder="Select status">
                    <Select.Option value="pending">Pending</Select.Option>
                    <Select.Option value="in_progress">In Progress</Select.Option>
                    <Select.Option value="completed">Completed</Select.Option>
                    <Select.Option value="cancelled">Cancelled</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name="remarks" label="Remarks">
                  <TextArea rows={4} placeholder="Enter completion or follow-up remarks" />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={submitLoader} className="save_btn" block>
                  Update Task
                </Button>
              </Form>
            </Card>
          </Col>
        </Row>
      ) : (
        <Empty description="No job task details found" />
      )}
    </div>
  );
};

export default JobTaskView;
